import {
  BillingInterval,
  CatalogItemStatus,
  CatalogItemType,
  insertCatalogItemSchema,
  insertCatalogPriceWithoutItemSchema,
} from "@/api/db/schema/catalog";
import { catalogService } from "@/api/services/catalog.service";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

const createCatalogOfferSchema = z.object({
  item: insertCatalogItemSchema,
  price: insertCatalogPriceWithoutItemSchema,
  programIds: z.array(z.cuid2()).default([]),
});

const updateCatalogOfferSchema = z.object({
  catalogItemId: z.cuid2(),
  item: insertCatalogItemSchema,
  price: insertCatalogPriceWithoutItemSchema,
  programIds: z.array(z.cuid2()).default([]),
});

const deleteCatalogOfferSchema = z.object({
  catalogItemId: z.cuid2(),
});

const setCatalogItemStatusSchema = z.object({
  catalogItemId: z.cuid2(),
  status: z.enum([
    CatalogItemStatus.DRAFT,
    CatalogItemStatus.ACTIVE,
    CatalogItemStatus.ARCHIVED,
  ]),
});

export const listAllCatalogOffersFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { message: "Forbidden" } };
    }
    const offers = await catalogService.listAllOffers();
    return { status: 200, body: { offers, total: offers.length } };
  });

export const listAllCatalogOffersOptions = () =>
  queryOptions({
    queryKey: ["admin", "catalog"],
    queryFn: () => listAllCatalogOffersFn(),
  });

export const createCatalogOfferFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(createCatalogOfferSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { message: "Forbidden" } };
    }
    try {
      const item = await catalogService.createOffer(data);
      return {
        status: 201,
        body: { message: "Katalog ürünü oluşturuldu", item },
      };
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Katalog ürünü oluşturulamadı";
      return {
        status: 400,
        body: { message: msg },
      };
    }
  });

export const updateCatalogOfferFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(updateCatalogOfferSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { message: "Forbidden" } };
    }
    try {
      const item = await catalogService.updateOffer(data.catalogItemId, {
        item: data.item,
        price: data.price,
        programIds: data.programIds,
      });
      return {
        status: 200,
        body: { message: "Katalog ürünü güncellendi", item },
      };
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Katalog ürünü güncellenemedi";
      return {
        status: 400,
        body: { message: msg },
      };
    }
  });

export const deleteCatalogOfferFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteCatalogOfferSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { message: "Forbidden" } };
    }
    try {
      const item = await catalogService.deleteOffer(data.catalogItemId);
      return item
        ? { status: 200, body: { message: "Katalog ürünü silindi", item } }
        : { status: 404, body: { message: "Katalog ürünü bulunamadı" } };
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Katalog ürünü silinemedi";
      return {
        status: 400,
        body: { message: msg },
      };
    }
  });

export const setCatalogItemStatusFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(setCatalogItemStatusSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { message: "Forbidden" } };
    }
    const item = await catalogService.setItemStatus(
      data.catalogItemId,
      data.status,
    );
    return item
      ? { status: 200, body: { message: "Durum güncellendi", item } }
      : { status: 404, body: { message: "Katalog ürünü bulunamadı" } };
  });

export { BillingInterval, CatalogItemStatus, CatalogItemType };
